import { useEffect, useRef } from 'react';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { useAppSelector } from 'store/hook';
import DispatchMonitorAPI from 'api/dispatch-monitor.api';

const TABLET_HEARTBEAT_EVENT = 'TABLET_HEARTBEAT';

export interface TabletHeartbeat {
  name: string;
  device: string;
  tabletId: string;
  asset: string;
  assetId: string;
  appVersions: {
    manager: string;
    gui: string;
    edgeservice: string;
  };
  batteryLevel: number;
  chargingState: string;
  networkType: string;
  iotConnected: boolean;
  tcpConnectedToDaemon: boolean;
  edgeServiceReachable: boolean;
  edgeServiceRunning: boolean;
  guiAppRunning: boolean;
  crashCount: number;
  uptime: number;
  freeStorageBytes: number;
  iotRxBytes: number;
  dataFreshnessS: number;
  lastValidIotMessageMs: number;
  timestamp: number;
  sensor_ts: number;
  lastUpdateResult: string;
}

/**
 * Opens an SSE connection to /api/dispatch-monitor/live-dispatch-sse and
 * invokes `onHeartbeat` with the payload of every TABLET_HEARTBEAT message
 * on the `xtab:{customerCode}:last:pubsub` channel — the same channel
 * DispatchMonitorView reads other action types (HEALTH_CHECK, SHIFT_END,
 * STATUS, ...) from. This hook just filters that stream down to the
 * TABLET_HEARTBEAT events.
 *
 * Modeled on useDeviceLastSensorSSE — same connect/parse/cleanup shape.
 */
export function useTabletHeartbeatSSE(
  onHeartbeat: (heartbeat: TabletHeartbeat) => void
) {
  const accessToken = useAppSelector((s) => s.authReducer.accessToken);
  const customerId = useAppSelector((s) => s.persistedReducer.customer.id);
  const customerCode = useAppSelector((s) => s.persistedReducer.customer.code);
  const dispatchMonitorApi = useRef(new DispatchMonitorAPI()).current;
  const esRef = useRef<InstanceType<typeof EventSourcePolyfill> | null>(null);
  const onHeartbeatRef = useRef(onHeartbeat);
  onHeartbeatRef.current = onHeartbeat;

  useEffect(() => {
    if (!accessToken || !customerId || !customerCode) return;

    const url = dispatchMonitorApi.liveDispatchSSEUrl(customerId);
    const channel = `xtab:${customerCode}:last:pubsub`;

    const es = new EventSourcePolyfill(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    esRef.current = es;

    es.onmessage = function (e: any) {
      try {
        const outer = JSON.parse(e.data as string);
        if (outer?.channel !== channel) return;

        const msg: TabletHeartbeat =
          typeof outer.message === 'string'
            ? JSON.parse(outer.message)
            : outer.message;

        if (msg?.name !== TABLET_HEARTBEAT_EVENT || !msg?.device) return;

        onHeartbeatRef.current(msg);
      } catch (err) {
        console.error('[tablet-heartbeat-sse] parse error:', err);
      }
    };

    es.onerror = () => {
      console.warn('[tablet-heartbeat-sse] connection error — reconnecting');
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [accessToken, customerId, customerCode, dispatchMonitorApi]);
}
